import { ErrorView } from "@/components/ui/layout/error/errorView"
import { logout401Function } from "@/modules/auth/hooks/useLogout"
import { HUError, HUErrorCode } from "@/modules/error/huError"
import { Path } from "@/modules/navigation/routes"
import { type ReactNode, useEffect } from "react"
import { ErrorBoundary, type FallbackProps } from "react-error-boundary"
import { Navigate, useLocation } from "react-router"

const isAuthError = (error: unknown) =>
  error instanceof HUError &&
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  (error.code === HUErrorCode.Unauthorised || error.code === HUErrorCode.TokenExpired)

/**
 * Top-level fallback. useUser() uses useSuspenseQuery, which always rethrows query errors to the
 * nearest ErrorBoundary (throwOnError in defaultOptions does NOT apply to suspense queries). On a
 * 401 the request layer rejects with HUError(Unauthorised); without a boundary here that throw
 * unmounts the whole React tree -> white screen. Here we clear the stale session and redirect to
 * login instead of crashing.
 */
const GlobalErrorFallback = ({ error }: FallbackProps) => {
  const authError = isAuthError(error)

  useEffect(() => {
    if (authError) void logout401Function()
  }, [authError])

  if (authError) return <Navigate to={Path.Login} replace />

  return <ErrorView />
}

export const GlobalErrorBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  // Reset the boundary on navigation so a recovered route renders fresh instead of staying on the fallback.
  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback} resetKeys={[location.key]}>
      {children}
    </ErrorBoundary>
  )
}
